/**
 * @file
 * Animate-on-scroll: toggles animate.css classes when [data-animate]
 * elements enter the viewport. Two patterns are supported:
 *
 *   1. Single element:
 *      <div data-animate="fadeInUp"
 *           data-animate-delay="0.3s"
 *           data-animate-duration="1s">
 *
 *   2. Staggered children (for grids/lists):
 *      <div data-animate-stagger="slideInUp"
 *           data-animate-stagger-step="0.1">
 *        <div data-stagger-item>...</div>
 *        <div data-stagger-item>...</div>
 *      </div>
 *
 *      When stagger items are not explicitly marked, the script falls
 *      back to direct children (drilling through a single Drupal
 *      `.field` wrapper if present).
 */

(function () {
  "use strict";

  var ROOT_CLASS = "js-animate-ready";
  var READY_CLASS = "animate-ready";
  var ANIMATED_CLASS = "animate__animated";
  var PREFIX = "animate__";
  var DEFAULT_ANIMATION = "fadeInUp";
  var DEFAULT_STAGGER_STEP = 0.1;

  function applyAnimation(el, animation, delay, duration) {
    if (delay) {
      el.style.animationDelay = typeof delay === "number" ? delay + "s" : delay;
    }
    if (duration) {
      el.style.animationDuration =
        typeof duration === "number" ? duration + "s" : duration;
    }
    el.classList.add(ANIMATED_CLASS, PREFIX + animation, READY_CLASS);
  }

  function reveal(el) {
    var animation = el.getAttribute("data-animate") || DEFAULT_ANIMATION;
    var delay = el.getAttribute("data-animate-delay");
    var duration = el.getAttribute("data-animate-duration");
    applyAnimation(el, animation, delay, duration);
  }

  function findStaggerItems(container) {
    // 1. Explicit opt-in via [data-stagger-item].
    var explicit = container.querySelectorAll("[data-stagger-item]");
    if (explicit.length) {
      return explicit;
    }

    // 2. Drupal field wrapper drill-through.
    var direct = container.children;
    if (direct.length === 1 && direct[0].classList.contains("field")) {
      var fieldItems = direct[0].querySelectorAll(":scope > .field__item");
      if (fieldItems.length) {
        return fieldItems;
      }
    }

    // 3. Direct children.
    return direct;
  }

  function revealStagger(container) {
    var animation =
      container.getAttribute("data-animate-stagger") || DEFAULT_ANIMATION;
    var step =
      parseFloat(container.getAttribute("data-animate-stagger-step")) ||
      DEFAULT_STAGGER_STEP;
    var duration = container.getAttribute("data-animate-duration");
    var items = findStaggerItems(container);

    for (var i = 0; i < items.length; i++) {
      applyAnimation(items[i], animation, i * step, duration);
    }
    container.classList.add(READY_CLASS);
  }

  function init() {
    var elements = document.querySelectorAll(
      "[data-animate]:not(." +
        READY_CLASS +
        "), [data-animate-stagger]:not(." +
        READY_CLASS +
        ")"
    );
    if (!elements.length) {
      return;
    }

    // Mark <html> so SCSS knows JS is active and can hide pre-animated elements.
    document.documentElement.classList.add(ROOT_CLASS);

    var prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      Array.prototype.forEach.call(elements, function (el) {
        el.classList.add(READY_CLASS);
        // Also mark stagger children visible so opacity:0 hide doesn't strand them.
        if (el.hasAttribute("data-animate-stagger")) {
          var items = findStaggerItems(el);
          for (var i = 0; i < items.length; i++) {
            items[i].classList.add(READY_CLASS);
          }
        }
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            return;
          }
          var target = entry.target;
          if (target.hasAttribute("data-animate-stagger")) {
            revealStagger(target);
          } else {
            reveal(target);
          }
          obs.unobserve(target);
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );

    Array.prototype.forEach.call(elements, function (el) {
      observer.observe(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
